import React, { useEffect, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { ChevronDown, ChevronUp, X, Check, Image as ImageIcon, Send, IndianRupee } from "lucide-react";
import StatCard from "../components/agentProgram/StatCard";
import StatusPill from "../components/agentProgram/StatusPill";
import EmptyState from "../components/agentProgram/EmptyState";

const API = process.env.REACT_APP_API_URL;
const SERVER_ROOT = (API || "").replace(/\/api\/?$/, "");

const money = (value) =>
  Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

const dateText = (value) =>
  value
    ? new Date(value).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : "-";

const txnStatusLabel = {
  payable: "Payable",
  released: "Released (held period matured)",
};

const requestStatusLabel = {
  pending: "Pending",
  fulfilled: "Fulfilled",
  dismissed: "Dismissed",
};

function ReleaseModal({ agentName, transactions, onClose, onSubmit, submitting, error }) {
  const [screenshot, setScreenshot] = useState(null);
  const total = transactions.reduce(
    (sum, t) => sum + (t.status === "released" ? (t.heldAmount || 0) : (t.availableAmount || 0)),
    0
  );

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-md overflow-hidden shadow-lg">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-lg bg-emerald-50 flex items-center justify-center">
              <Check size={15} className="text-emerald-600" />
            </div>
            <h3 className="text-sm font-semibold text-gray-900">Release Payout — {agentName}</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700">
            <X size={18} />
          </button>
        </div>

        <div className="p-5">
          <div className="bg-gray-50 border border-gray-200 rounded-lg px-3.5 py-2.5 mb-4">
            <p className="text-sm text-gray-700">
              {transactions.length} transaction{transactions.length !== 1 ? "s" : ""} selected
            </p>
            <p className="text-lg font-semibold text-gray-900">₹{money(total)}</p>
          </div>

          <label className="block mb-1">
            <span className="block text-xs font-medium text-gray-600 mb-1">Screenshot</span>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setScreenshot(e.target.files?.[0] || null)}
              className="block w-full text-sm border border-gray-200 rounded-lg px-3 py-2"
            />
          </label>
          <p className="text-xs text-gray-400 mb-4">Required — proof of transfer, shown to the agent once released.</p>

          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 text-xs mb-3">{error}</div>}

          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="text-sm px-4 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50">
              Cancel
            </button>
            <button
              onClick={() => screenshot && onSubmit(screenshot)}
              disabled={!screenshot || submitting}
              className="text-sm px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white shadow-sm hover:shadow transition-all"
            >
              {submitting ? "Releasing…" : "Release"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AgentPayoutRequests() {
  const { userInfo } = useSelector((state) => state.userLogin || {});
  const [tab, setTab] = useState("pending"); // 'pending' | 'history'
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [expandedAgent, setExpandedAgent] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loadingTxns, setLoadingTxns] = useState(false);
  const [selectedTxnIds, setSelectedTxnIds] = useState([]);
  const [releaseModalAgent, setReleaseModalAgent] = useState(null);
  const [releasing, setReleasing] = useState(false);
  const [releaseError, setReleaseError] = useState("");

  const [history, setHistory] = useState({ requests: [], releaseBatches: [] });
  const [loadingHistory, setLoadingHistory] = useState(false);

  const authConfig = { headers: userInfo?.token ? { Authorization: `Bearer ${userInfo.token}` } : {} };

  const fetchRequests = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${API}/agent-program/payout-requests`, authConfig);
      setRequests(data || []);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Failed to load payout requests");
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    setLoadingHistory(true);
    setError("");
    try {
      const { data } = await axios.get(`${API}/agent-program/payout-history`, authConfig);
      setHistory({ requests: data?.requests || [], releaseBatches: data?.releaseBatches || [] });
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Failed to load payout history");
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    if (tab === "pending") fetchRequests();
    else fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const toggleExpand = async (row) => {
    const agentId = row.agent?._id;
    if (!agentId) return;
    if (expandedAgent === agentId) {
      setExpandedAgent(null);
      setTransactions([]);
      setSelectedTxnIds([]);
      return;
    }
    setExpandedAgent(agentId);
    setSelectedTxnIds([]);
    setLoadingTxns(true);
    setTransactions([]);
    try {
      const { data } = await axios.get(`${API}/agent-program/agents/${agentId}/transactions`, authConfig);
      setTransactions((data || []).filter((t) => t.status === "payable" || t.status === "released"));
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Failed to load transactions");
    } finally {
      setLoadingTxns(false);
    }
  };

  const toggleSelect = (txnId) => {
    setSelectedTxnIds((prev) => (prev.includes(txnId) ? prev.filter((id) => id !== txnId) : [...prev, txnId]));
  };

  const openReleaseModal = (row) => {
    if (selectedTxnIds.length === 0) return;
    setReleaseError("");
    setReleaseModalAgent(row);
  };

  const submitRelease = async (screenshotFile) => {
    const agentId = releaseModalAgent?.agent?._id;
    if (!agentId) return;
    setReleasing(true);
    setReleaseError("");
    try {
      const form = new FormData();
      form.append("agentId", agentId);
      form.append("transactionIds", JSON.stringify(selectedTxnIds));
      form.append("screenshot", screenshotFile);
      await axios.post(`${API}/agent-program/payouts/release-batch`, form, {
        headers: { ...authConfig.headers, "Content-Type": "multipart/form-data" },
      });
      setMessage(`Released ${selectedTxnIds.length} transaction(s) for ${releaseModalAgent.agent?.name || "agent"}.`);
      setReleaseModalAgent(null);
      setSelectedTxnIds([]);
      setExpandedAgent(null);
      setTransactions([]);
      await fetchRequests();
    } catch (err) {
      setReleaseError(err?.response?.data?.message || err.message || "Failed to release payout");
    } finally {
      setReleasing(false);
    }
  };

  const dismissRequest = async (row) => {
    const agentId = row.agent?._id;
    if (!agentId) return;
    setError("");
    setMessage("");
    try {
      await axios.post(`${API}/agent-program/agents/${agentId}/dismiss-payout-request`, {}, authConfig);
      setMessage(`Dismissed request from ${row.agent?.name || "agent"}.`);
      if (expandedAgent === agentId) {
        setExpandedAgent(null);
        setTransactions([]);
      }
      await fetchRequests();
    } catch (err) {
      setError(err?.response?.data?.message || err.message || "Failed to dismiss request");
    }
  };

  // Merge requests + release batches into one chronological feed for the History tab.
  const timeline = [
    ...history.requests.map((r) => ({ kind: "request", at: r.requestedAt, data: r })),
    ...history.releaseBatches.map((b) => ({ kind: "release", at: b.releasedAt, data: b })),
  ].sort((a, b) => new Date(b.at) - new Date(a.at));

  const pendingCount = requests.length;
  const pendingAmount = requests.reduce((sum, r) => sum + (r.availableBalance || 0), 0);

  return (
    <div className="-mx-3 px-2 py-3 sm:mx-0 sm:p-5 bg-gray-50 min-h-screen w-[calc(100%+1.5rem)] sm:w-full">
      <div className="flex items-center gap-3 mb-5">
        <div className="h-11 w-11 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
          <Send size={20} className="text-blue-600" />
        </div>
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">Agent Payout Requests</h1>
          <p className="text-sm text-gray-500 mt-0.5">Requests waiting on admin, and the full request/release history.</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-5 max-w-md">
        <StatCard title="Pending Requests" value={pendingCount} icon={Send} tone="bg-amber-50 text-amber-600" />
        <StatCard title="Pending Amount" value={`₹${money(pendingAmount)}`} icon={IndianRupee} tone="bg-blue-50 text-blue-600" />
      </div>

      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setTab("pending")}
          className={`text-sm px-4 py-2 rounded-lg ${tab === "pending" ? "bg-gray-900 text-white" : "bg-white border border-gray-200 text-gray-600"}`}
        >
          Pending
        </button>
        <button
          onClick={() => setTab("history")}
          className={`text-sm px-4 py-2 rounded-lg ${tab === "history" ? "bg-gray-900 text-white" : "bg-white border border-gray-200 text-gray-600"}`}
        >
          History
        </button>
      </div>

      {message && <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-lg px-4 py-3 text-sm mb-4">{message}</div>}
      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm mb-4">{error}</div>}

      {tab === "pending" ? (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {loading ? (
            <EmptyState icon={Send} title="Loading…" compact />
          ) : requests.length === 0 ? (
            <EmptyState icon={Send} title="No pending payout requests" compact />
          ) : (
            <div className="divide-y divide-gray-100">
              {requests.map((row) => {
                const agentId = row.agent?._id;
                const isExpanded = expandedAgent === agentId;
                return (
                  <div key={agentId || row._id}>
                    <div className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-gray-50">
                      <button onClick={() => toggleExpand(row)} className="flex items-center gap-3 flex-1 min-w-0 text-left">
                        {isExpanded ? <ChevronUp size={16} className="text-gray-400 flex-shrink-0" /> : <ChevronDown size={16} className="text-gray-400 flex-shrink-0" />}
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-gray-800 truncate">{row.agent?.name || "Unknown agent"}</div>
                          <div className="text-xs text-gray-400 truncate">
                            {[row.agent?.identifier, row.agent?.contactNumber].filter(Boolean).join(" · ")}
                          </div>
                        </div>
                      </button>
                      <div className="text-right flex-shrink-0">
                        <div className="text-sm font-semibold text-gray-900">₹{money(row.availableBalance)}</div>
                        <div className="text-xs text-gray-400">requested {dateText(row.payoutRequestedAt)}</div>
                      </div>
                      <button
                        onClick={() => dismissRequest(row)}
                        title="Dismiss request"
                        className="flex-shrink-0 text-gray-400 hover:text-gray-700 p-1.5 rounded-lg hover:bg-gray-100"
                      >
                        <X size={16} />
                      </button>
                    </div>

                    {isExpanded && (
                      <div className="bg-gray-50 px-4 py-3 border-t border-gray-100">
                        {loadingTxns ? (
                          <div className="text-sm text-gray-400 py-2">Loading transactions…</div>
                        ) : transactions.length === 0 ? (
                          <div className="text-sm text-gray-400 py-2">Nothing currently releasable for this agent.</div>
                        ) : (
                          <>
                            <div className="space-y-2 mb-3">
                              {transactions.map((t) => (
                                <label
                                  key={t._id}
                                  className="flex items-center gap-3 bg-white rounded-lg border border-gray-200 px-3 py-2 cursor-pointer"
                                >
                                  <input
                                    type="checkbox"
                                    checked={selectedTxnIds.includes(t._id)}
                                    onChange={() => toggleSelect(t._id)}
                                  />
                                  <div className="min-w-0 flex-1">
                                    <div className="text-xs font-medium text-gray-700">
                                      #{t.serialNumber} · {t.type.replace(/_/g, " ")}
                                    </div>
                                    <div className="text-xs text-gray-400">{txnStatusLabel[t.status] || t.status}</div>
                                  </div>
                                  <span className="text-sm font-semibold text-gray-900">
                                    ₹{money(t.status === "released" ? t.heldAmount : t.availableAmount)}
                                  </span>
                                </label>
                              ))}
                            </div>
                            <button
                              onClick={() => openReleaseModal(row)}
                              disabled={selectedTxnIds.length === 0}
                              className="inline-flex items-center gap-1 text-xs bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white px-3 py-2 rounded-lg"
                            >
                              <Check size={13} /> Release Selected ({selectedTxnIds.length})
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {loadingHistory ? (
            <EmptyState icon={ImageIcon} title="Loading…" compact />
          ) : timeline.length === 0 ? (
            <EmptyState icon={ImageIcon} title="No payout activity yet" compact />
          ) : (
            <div className="divide-y divide-gray-100">
              {timeline.map((item, idx) =>
                item.kind === "request" ? (
                  <div key={`req-${item.data._id || idx}`} className="flex items-center justify-between gap-3 px-4 py-3">
                    <div className="min-w-0">
                      <div className="text-sm text-gray-800">
                        <span className="font-medium">{item.data.agent?.name || "Agent"}</span> requested ₹{money(item.data.requestedAmount)}
                      </div>
                      <div className="text-xs text-gray-400">{dateText(item.data.requestedAt)}</div>
                    </div>
                    <StatusPill
                      label={requestStatusLabel[item.data.status] || item.data.status}
                      tone={item.data.status === "pending" ? "warning" : item.data.status === "fulfilled" ? "success" : "neutral"}
                    />
                  </div>
                ) : (
                  <div key={`rel-${item.data._id || idx}`} className="flex items-center justify-between gap-3 px-4 py-3">
                    <div className="min-w-0">
                      <div className="text-sm text-gray-800">
                        <span className="font-medium">{item.data.agent?.name || "Agent"}</span> released ₹{money(item.data.netAmount)}
                        {item.data.shortfallRecovered > 0 && (
                          <span className="text-xs text-orange-500"> (₹{money(item.data.shortfallRecovered)} recovered from shortfall)</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-400">
                        batch #{item.data.serialNumber} · by {item.data.releasedBy?.name || "admin"} · {dateText(item.data.releasedAt)}
                      </div>
                    </div>
                    {item.data.screenshotUrl && (
                      <a
                        href={`${SERVER_ROOT}${item.data.screenshotUrl}`}
                        target="_blank"
                        rel="noreferrer"
                        className="flex-shrink-0 inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                      >
                        <ImageIcon size={14} /> View proof
                      </a>
                    )}
                  </div>
                )
              )}
            </div>
          )}
        </div>
      )}

      {releaseModalAgent && (
        <ReleaseModal
          agentName={releaseModalAgent.agent?.name || "Agent"}
          transactions={transactions.filter((t) => selectedTxnIds.includes(t._id))}
          onClose={() => setReleaseModalAgent(null)}
          onSubmit={submitRelease}
          submitting={releasing}
          error={releaseError}
        />
      )}
    </div>
  );
}
